import { useTRPC } from '@/utils/trpc';
import { QueryStateHandler } from '@liza/ui/components/QueryStateHandler';
import { QuestionnaireResponseAnswers } from '@liza/ui/components/records/QuestionnaireResponseAnswers';
import { Spinner } from '@liza/ui/components/ui/spinner';
import { resolveId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

type Props = {
  observation: Observation;
};

/**
 * Per-question detail for one survey result: walks `Observation.derivedFrom` to the
 * QuestionnaireResponse and resolves its Questionnaire for question text. Mounted lazily —
 * only when the result row is expanded — so nothing is fetched for collapsed rows.
 */
export const SurveyResponseContent = ({ observation }: Props): React.ReactNode => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();

  const responseRef = observation.derivedFrom?.find((ref) => ref.reference?.startsWith('QuestionnaireResponse/'));
  const responseId = resolveId(responseRef);

  const responseQuery = useQuery({
    ...trpc.patient.questionnaireResponse.get.queryOptions({ id: responseId ?? '' }),
    enabled: Boolean(responseId),
  });

  // Question text lives on the Questionnaire, not the QR — resolve it before rendering.
  const canonical = responseQuery.data?.questionnaireResponse.questionnaire;
  const questionnaireQuery = useQuery({
    ...trpc.patient.questionnaire.getByUrl.queryOptions({ url: canonical ?? '' }),
    enabled: Boolean(canonical),
  });

  if (!responseId) {
    return <p className="text-sm text-muted-foreground">{t('noResponseOnRecord')}</p>;
  }

  // Hold off rendering until the Questionnaire resolves, so question text appears in one
  // pass rather than flashing raw linkIds first.
  if (questionnaireQuery.isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner className="size-6" />
      </div>
    );
  }

  return (
    <QueryStateHandler query={responseQuery} emptyMessage={t('noResponseOnRecord')}>
      {(data) => {
        const questionnaire = questionnaireQuery.data?.questionnaire;
        if (!questionnaire) {
          // The response exists but its Questionnaire definition couldn't be loaded — a data
          // or network issue, distinct from there being no response at all.
          return <p className="text-sm text-muted-foreground">{t('unableToLoadQuestions')}</p>;
        }
        return (
          <QuestionnaireResponseAnswers
            questionnaireResponse={data.questionnaireResponse}
            questionnaire={questionnaire}
          />
        );
      }}
    </QueryStateHandler>
  );
};
